import i18n from 'i18next';
import React, { useEffect, useState, useContext, createContext } from 'react';
import awsConfig from '../aws-exports';
import { clearStorage, showNotificationDialog } from '../utils';
import { Amplify, Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { Hub } from 'aws-amplify';
import useDevice from '../hooks/useDevice';
import dayjs from 'dayjs';
import { notification } from 'antd';
import { DURATION_TURN_OFF_DIALOG } from '../utils/Constants';

const updatedAwsConfig = {
    ...awsConfig,
    oauth: {
        ...awsConfig.oauth,
        redirectSignIn: `${window.location.origin}/`,
        redirectSignOut: `${window.location.origin}/`,
    },
};

//Cognitoの設定
Amplify.configure(updatedAwsConfig);

const AuthContext = createContext<any>(null);

export const ProvideAuth = ({ children }: { children: React.ReactNode }) => {
    const auth = useProvideAuth();
    return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useFujiAuth = () => {
    return useContext(AuthContext);
};

type FujiUser = {
    providerName: string | null;
    email: string;
    username: string;
    userId: string;
    accessToken: string;
    idToken: string;
    refreshToken: string;
} | null;

const useProvideAuth = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [tempEmail, setTempEmail] = useState('');
    const [tempPassword, setTempPassword] = useState('');
    const [user, setUser] = useState<FujiUser>(null);
    const { t } = useTranslation();
    const { createDeviceId } = useDevice();

    useEffect(() => {
        Hub.listen('auth', ({ payload: { event, data } }) => {
            if (event === 'signIn') {
                // 現在時刻を取得
                const now = dayjs.utc();
                // ローカルストレージに保存
                localStorage.setItem('LAST_LOGIN_TIME', now.toISOString());

                Auth.currentAuthenticatedUser({ bypassCache: true })
                    .then((res) => {
                        const userSession = res && (res as any).signInUserSession;
                        if (!userSession) {
                            console.error('Cannot get login provider: signInUserSession is not available.');
                            return;
                        }
                        const { idToken, accessToken, refreshToken } = userSession;
                        const identities = idToken?.payload?.identities;

                        let provider = 'Email';
                        if (identities && identities.length > 0) {
                            provider = identities[0].providerName;
                        }

                        // ローカルストレージにログイン方法を保存
                        localStorage.setItem('LAST_LOGIN_METHOD', provider);

                        const signedInUser = {
                            providerName: identities?.[0]?.providerName ?? null,
                            email: idToken.payload.email,
                            username: idToken.payload.preferred_username,
                            userId: idToken.payload.sub,
                            accessToken: accessToken.jwtToken,
                            idToken: idToken.jwtToken,
                            refreshToken: refreshToken.token,
                        };
                        setUser(signedInUser);
                        setIsAuthenticated(true);
                    })
                    .catch((err) => {
                        console.error('Cannot get login provider:', err);
                    });
            }
            //インターネット状態をチェック、オンライすればログアウトを実行
            // tokenRefresh_failure,tokenRefresh
            // 勝手にログアウトする問題があるので、とりあえず無効化 (240620)
            if (event.includes('tokenRefresh_failure')) {
                // MFP-2670; トークン更新失敗時にログアウトする
                // パターン1: 通信不良によるエラー
                if (
                    // パターン1-1: 明示的なネットワークエラー
                    (typeof data.code === 'string' && data.code.match(/.*network.*/gi) !== null) ||
                    // パターン1-2: 暗黙的なネットワークエラー
                    // - Amplfy内部で認証結果の解析に失敗した場合
                    // - サーバーからの応答が無効な場合
                    (typeof data.message === 'string' && data.message.match(/.*(undefined|null).*/gi) !== null)
                ) {
                    return;
                }
                // パターン2: その他Cognitoの例外発生時 (e.g. トークン期限切れ、無効なトークンの再利用など)
                else {
                    //自動ログアウト処理
                    void (async () => {
                        localStorage.setItem('AUTO_LOGOUT', 'true');
                        try {
                            await Auth.signOut({ global: false });
                            await setLogoutStatus();
                        } catch {}
                    })();
                }
            }
            // Hub経由でサインアウト成功の後に状態を設定
            if (event.includes('signOut')) {
                if (localStorage.getItem('AUTO_LOGOUT')) {
                    localStorage.removeItem('AUTO_LOGOUT');
                    const timeInterval = setInterval(function () {
                        notification.error({
                            key: 'auto_logout',
                            duration: DURATION_TURN_OFF_DIALOG,
                            message: t('notification.title'),
                            description: t('error-message.logout.auto-logout'),
                        });
                        clearInterval(timeInterval);
                    }, 2000);
                }
                // ローカルストレージにログイン時刻を削除
                localStorage.removeItem('LAST_LOGIN_TIME');
                // 一時的メールを削除
                setTempEmail('');
                // ユーザ情報を削除
                setUser(null);
                // ログインスターテスが「false」にする
                setIsAuthenticated(false);
            }
        });
        // 最初の認証確認
        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then((res) => {
                const { idToken, accessToken, refreshToken } = res.signInUserSession;
                const user = {
                    providerName: (idToken.payload.identities && idToken.payload.identities[0].providerName) ?? null,
                    email: idToken.payload.email,
                    username: idToken.payload.preferred_username,
                    userId: idToken.payload.sub,
                    accessToken: accessToken.jwtToken,
                    idToken: idToken.jwtToken,
                    refreshToken: refreshToken.token,
                };
                //ユーザーがログインしているかどうかをチェック
                if (localStorage.getItem('LAST_LOGIN_TIME')) {
                    setIsAuthenticated(true);
                    setUser(user);
                    localStorage.setItem('LAST_LOGIN_METHOD', user.providerName ?? 'Email');
                }
                setIsLoading(false);
            })
            .catch(() => {
                // 最新のユーザー情報更新に失敗したとき、無条件でログアウトしない
                // setIsAuthenticated(false);
                // setUser(null);
                setIsLoading(false);
                if (localStorage.getItem('isDeleteUser')) {
                    setIsAuthenticated(false);
                    setUser(null);
                    localStorage.removeItem('isDeleteUser');
                    // ローカルストレージにデータを削除
                    void clearStorage();
                    //退会完成
                    showNotificationDialog(
                        'success',
                        'delete-account',
                        t('notification.title'),
                        t('notification.delete-acc-success'),
                    );
                }
                if (localStorage.getItem('isSocialLogin')) {
                    setIsAuthenticated(false);
                    setUser(null);
                    // ソーシャルログイン時にエラーが発生した場合
                    localStorage.removeItem('isSocialLogin');
                    // エラーを表示
                    showNotificationDialog(
                        'error',
                        'social_login_failed',
                        t('notification.title'),
                        t('error-message.login.failed'),
                    );
                }
                if (
                    localStorage.getItem('deleteUser.error') !== null &&
                    localStorage.getItem('deleteUser.error')?.includes('UserNotFoundException')
                ) {
                    localStorage.removeItem('deleteUser.error');
                    // 既に退会済みのエラーメッセンジを表示
                    showNotificationDialog(
                        'failed',
                        'delete-account',
                        t('notification.title'),
                        t('error-message.delete-account.no-user'),
                    );
                }
            });
    }, [t]);

    /**
     * 新規登録の処理
     *
     * @param {String} email
     * @param {String} password
     * @param {Function} callback
     */
    const signUp = (
        email: string,
        password: string,
        callback: (result: { isSuccessed: boolean; message: string; error?: any }) => void,
    ) => {
        Auth.signUp({
            username: email,
            password: password,
            attributes: { 'custom:language': i18n.language },
        })
            .then(() => {
                setTempEmail(email);
                setTempPassword(password);
                callback({ isSuccessed: true, message: '' });
            })
            .catch((error) => {
                console.log('Signup error ', error);
                callback({
                    isSuccessed: false,
                    message: error.message,
                    error: error,
                });
            });
    };

    /**
     * コードの認証の処理
     *
     * @param {String} verificationCode
     * @param {Function} callback
     */
    const confirmSignUp = (
        verificationCode: string,
        callback: (result: { isSuccessed: boolean; message: string; error?: any; onSignIn?: boolean }) => void,
    ) => {
        Auth.confirmSignUp(tempEmail, verificationCode)
            .then((res) => {
                console.log('Res ', res);
                signIn(tempEmail, tempPassword, (result: any) => {
                    callback({ ...result, onSignIn: true });
                });
            })
            .catch((error) => {
                console.log('認証コードのエラー ', error);
                callback({
                    isSuccessed: false,
                    message: '認証に失敗しました。',
                    error: error,
                });
            });
    };

    /**
     * サインアップ確認コードを再送信する
     *
     * @param {String} username
     * @param {Function} callback
     */
    const resendConfirmationCode = (
        username: string,
        callback: (result: { isSuccessed: boolean; message: string; error?: any }) => void,
    ) => {
        Auth.resendSignUp(username)
            .then(() => {
                // console.log('code resent successfully!!!');
                callback({ isSuccessed: true, message: '' });
            })
            .catch((error) => {
                console.log('error resending code: ', error);
                callback({ isSuccessed: false, message: '', error: error });
            });
    };

    /**
     * ログインの処理
     *
     * @param {String} email
     * @param {String} password
     * @param {Function} callback
     */
    const signIn = (
        email: string,
        password: string,
        callback: (result: { isSuccessed: boolean; message: string }) => void,
    ) => {
        Auth.signIn(email, password)
            .then((res) => {
                const { idToken, accessToken, refreshToken } = res.signInUserSession;
                const user = {
                    providerName: (idToken.payload.identities && idToken.payload.identities[0].providerName) ?? null,
                    email: idToken.payload.email,
                    username: idToken.payload.preferred_username,
                    userId: idToken.payload.sub,
                    accessToken: accessToken.jwtToken,
                    idToken: idToken.jwtToken,
                    refreshToken: refreshToken.token,
                };
                setIsAuthenticated(true);
                setUser(user);
                setTempEmail('');
                setTempPassword('');
                createDeviceId();
                callback({ isSuccessed: true, message: '' });
            })
            .catch((error) => {
                console.log({ email }, { password });
                console.log('メールでログインのエラー ', error.message);
                if (error.message.includes('User is not confirmed')) {
                    setTempEmail(email);
                    setTempPassword(password);
                }
                callback({
                    isSuccessed: false,
                    message: error.message,
                });
            });
    };

    /**
     *  メールアドレスを変更の実行
     *
     * @param {Object} attributes
     * @param {Function} callback
     */
    const updateUserAttributes = (
        attributes: object,
        callback: (result: { isSuccessed: boolean; message: string }) => void,
    ) => {
        Auth.currentAuthenticatedUser({ bypassCache: true })
            .then((user) => {
                return Auth.updateUserAttributes(user, { ...attributes, 'custom:language': i18n.language });
            })
            .then(() => {
                // console.log('udpateAttributes ', res);
                callback({
                    isSuccessed: true,
                    message: '',
                });
            })
            .catch((error) => {
                // console.log('udpateAttributes ', error);
                callback({
                    isSuccessed: false,
                    message: error,
                });
            });
    };

    /**
     * メールを変更の確認コードを認証の実行
     *
     * @param {String} code
     * @param {Function} callback
     */
    const verifyEmailValidationCode = (
        code: string,
        callback: (result: { isSuccessed: boolean; message: string }) => void,
    ) => {
        Auth.verifyCurrentUserAttributeSubmit('email', code)
            .then(() => {
                return Auth.currentAuthenticatedUser({ bypassCache: true });
            })
            .then((res) => {
                const { idToken, accessToken, refreshToken } = res.signInUserSession;
                const user = {
                    providerName: (idToken.payload.identities && idToken.payload.identities[0].providerName) ?? null,
                    email: idToken.payload.email,
                    username: idToken.payload.preferred_username,
                    userId: idToken.payload.sub,
                    accessToken: accessToken.jwtToken,
                    idToken: idToken.jwtToken,
                    refreshToken: refreshToken.token,
                };
                setIsAuthenticated(true);
                setUser(user);
                callback({
                    isSuccessed: true,
                    message: '',
                });
            })
            .catch((error) => {
                console.log('failed with error', error);
                callback({
                    isSuccessed: false,
                    message: error,
                });
            });
    };

    /**
     * パスワードの変更処理
     *
     * @param {String} oldPassword
     * @param {String} newPassword
     * @param {Function} callback
     */
    const changePassword = (
        oldPassword: string,
        newPassword: string,
        callback: (result: { isSuccessed: boolean; message: string }) => void,
    ) => {
        Auth.currentAuthenticatedUser()
            .then((user) => {
                return Auth.changePassword(user, oldPassword, newPassword);
            })
            .then((res) => {
                console.log('Change password successfully ', res);
                callback({
                    isSuccessed: true,
                    message: '',
                });
            })
            .catch((error) => {
                console.log('change password error ', error);
                callback({
                    isSuccessed: false,
                    message: error,
                });
            });
    };

    /**
     * ユーザーのメールに確認コードを送信する
     * @param {String} email
     */
    const forgotPassword = (
        email: string,
        callback: (result: { isSuccessed: boolean; message: string; error?: any }) => void,
    ) => {
        // Send confirmation code to user's email
        const clientMetaData = { language: i18n.language };
        Auth.forgotPassword(email, clientMetaData)
            .then(() => {
                callback({
                    isSuccessed: true,
                    message: '',
                });
            })
            .catch((error) => {
                // console.log('error ', error);
                callback({
                    isSuccessed: false,
                    message: error.message,
                    error: error,
                });
            });
    };

    /**
     * 再設定パスワードの処理
     *
     * @param {String} email
     * @param {String} code
     * @param {String} new_password
     * @param {Function} callback
     */
    const forgotPasswordSubmit = (
        email: string,
        code: string,
        new_password: string,
        callback: (result: { isSuccessed: boolean; message: string; error?: any }) => void,
    ) => {
        Auth.forgotPasswordSubmit(email, code, new_password)
            .then((res) => {
                console.log('forgotPasswordSubmit', res);
                callback({
                    isSuccessed: true,
                    message: '',
                });
            })
            .catch((error) => {
                console.log('forgotPasswordSubmit ', error);
                callback({
                    isSuccessed: false,
                    message: error.message,
                    error: error,
                });
            });
    };

    /**
     * ログアウトの処理
     *
     * @param {Function} callback
     */
    const signOut = (isGlobal = false, callback: (result: { isSuccessed: boolean; message: string }) => void) => {
        Auth.signOut({ global: isGlobal })
            .then(() => {
                // ローカルストレージにデータを削除
                void clearStorage();
                // 一時的メールを削除
                setTempEmail('');
                // ユーザ情報を削除
                setUser(null);
                // ログインスターテスが「false」にする
                setIsAuthenticated(false);
                callback({ isSuccessed: true, message: '' });
            })
            .catch(() => {
                callback({
                    isSuccessed: false,
                    message: t('error-message.logout.failed'),
                });
            });
    };

    /**
     * アカウン退会の処理
     *
     * @param {Function} callback
     * @param {Function} deleteUserInfo
     */
    const deleteUser = async (
        callback: (result: { isSuccessed: boolean; message: string }) => void,
        deleteUserInfo: () => Promise<void> = async () => {},
    ) => {
        try {
            await deleteUserInfo();
            await Auth.deleteUser();
            // ローカルストレージにデータを削除
            void clearStorage();
            // 一時的メールを削除
            setTempEmail('');
            // ユーザ情報を削除
            setUser(null);
            // ログインスターテスが「false」にする
            setIsAuthenticated(false);

            callback({ isSuccessed: true, message: '' });
        } catch (error) {
            callback({ isSuccessed: false, message: error as string });
        }
    };

    /**
     * ログアウトスターテスを反映
     */
    const setLogoutStatus = async () => {
        // 一時的メールを削除
        setTempEmail('');
        // ユーザ情報を削除
        setUser(null);
        // ログインスターテスが「false」にする
        setIsAuthenticated(false);
    };

    return {
        isLoading,
        tempEmail,
        user,
        isAuthenticated,
        signIn,
        signUp,
        confirmSignUp,
        resendConfirmationCode,
        updateUserAttributes,
        verifyEmailValidationCode,
        changePassword,
        forgotPassword,
        forgotPasswordSubmit,
        signOut,
        deleteUser,
        setLogoutStatus,
    };
};
